import {Component, Input, OnInit} from '@angular/core';
import {UserMenuComponent} from '../user-menu/user-menu.component';
import {RouterLink, RouterLinkActive} from '@angular/router';
import {TranslocoPipe, TranslocoService} from '@jsverse/transloco';
import {DashboardOnboarding} from '../../entities/dashboard/onboarding.entity';

import {LogoComponent} from '../../../core/components/logo/logo.component';
import {SidebarTrial} from '../sidebar-trial/sidebar-trial';
import {AuthenticationService} from '../../../core/services/authentication/authentication.service';
import {User} from '../../../core/entities/user/user.entity';
import {CreatorDashboardService} from '../../services/creator/creator-dashboard.service';
import {SidebarNotifications} from '../../entities/dashboard/sidebar-notifications.entity';
import {IndividualConfig, ToastrService} from 'ngx-toastr';
import {BookingService} from '../../services/booking/booking.service';
import {PendingBooking} from '../../entities/booking/pending-booking.entity';
import {PendingBookingToastComponent, PendingBookingToastPayload} from './pending-booking-toast/pending-booking-toast.component';
import {BookingStatus} from '../../../core/entities/booking/booking.entity';
import {Router} from '@angular/router';
import {from, Observable, of, throwError} from 'rxjs';
import {catchError, tap} from 'rxjs/operators';

@Component({
  selector: 'app-sidebar',
  imports: [
    UserMenuComponent,
    RouterLink,
    RouterLinkActive,
    TranslocoPipe,
    LogoComponent,
    SidebarTrial,
  ],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss'
})
export class SidebarComponent implements OnInit {
  @Input() public onboarding: DashboardOnboarding|null = null;
  public user: User|null = null;
  public showTrial = false;
  public sidebarNotifications: SidebarNotifications|null = null;
  public sidebarIsOpened = false;
  private readonly shownPendingBookingIds = new Set<number>();

  public constructor(
    private readonly authenticationService: AuthenticationService,
    private readonly creatorDashboardService: CreatorDashboardService,
    private readonly transloco: TranslocoService,
    private readonly toastr: ToastrService,
    private readonly bookingService: BookingService,
    private readonly router: Router,
  ) {
  }

  public ngOnInit() {
    this.user = this.authenticationService.getCurrentUser();
    if (this.user !== null) {
      this.showTrial = this.user.isInTrial === true;
    }
    this.creatorDashboardService.getNotifications().subscribe(
      (notifications) => {
        this.sidebarNotifications = notifications;
      }
    );
    this.bookingService.getPendingBookings().subscribe(
      (pending) => this.showPendingBookingToasts(pending)
    );
  }

  public openSidebar(): void {
    this.sidebarIsOpened = true;
  }

  public closeSidebar(): void {
    this.sidebarIsOpened = false;
  }

  private showPendingBookingToasts(pendingBookings: PendingBooking[]): void {
    pendingBookings
      .filter((booking) => !this.shownPendingBookingIds.has(booking.id))
      .forEach((booking) => {
        this.shownPendingBookingIds.add(booking.id);
        this.toastr.show(
          '',
          '',
          this.buildPendingToastConfig(booking),
          'toast-pending-booking'
        );
      });
  }

  private buildPendingToastConfig(booking: PendingBooking): Partial<IndividualConfig<PendingBookingToastPayload>> {
    const payload: PendingBookingToastPayload = {
      booking,
      actions: {
        learnMore: () => this.handleLearnMore(booking.id),
        refuse: () => this.changePendingBookingStatus(booking.id, BookingStatus.cancelled),
        accept: () => this.changePendingBookingStatus(booking.id, BookingStatus.accepted),
      },
    };

    return {
      toastComponent: PendingBookingToastComponent,
      toastClass: 'booking-pending-toast',
      disableTimeOut: true,
      closeButton: false,
      tapToDismiss: false,
      payload
    };
  }

  private handleLearnMore(bookingId: number): Observable<unknown> {
    return from(this.router.navigate(['/manage/booking', bookingId]));
  }

  private changePendingBookingStatus(id: number, status: BookingStatus): Observable<unknown> {
    return this.bookingService.changeBookingStatus(id, status).pipe(
      tap(() => {
        this.toastr.success(
          this.transloco.translate('bookings.manage-status.toasts.success.content'),
          this.transloco.translate('bookings.manage-status.toasts.success.title')
        );
        if (this.sidebarNotifications?.numberOfBookingsPendingApproval && this.sidebarNotifications.numberOfBookingsPendingApproval > 0) {
          this.sidebarNotifications = {
            ...this.sidebarNotifications,
            numberOfBookingsPendingApproval: this.sidebarNotifications.numberOfBookingsPendingApproval - 1
          };
        }
      }),
      catchError((err) => {
        this.toastr.error(
          this.transloco.translate('bookings.manage-status.toasts.error.content'),
          this.transloco.translate('bookings.manage-status.toasts.error.title')
        );
        return throwError(() => err);
      })
    );
  }
}
